import { useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import * as K from 'common/constants';
import history from 'service/customHistory';
import Input from 'components/common/Input/Input';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import {
  COUNTRIES_WITH_STATES,
  MarketingFields,
  MARKETO_BASE_URL,
  MARKETO_MUNCHKIN_ID,
  TRIAL_FORM_ID,
} from 'components/standalonePages/common/constants';
import ELEMENT from 'common/element';
import { useMarketo } from 'hooks/useMarketo';

import { gql, gqlRequest } from 'common/util/gqlRequest';
import './FormPages.scss';
import MarketingForm from './common/MarketingForm';

export type SignUpFields = {
  email: string;
  password: string;
};

export type MarketingSignUpFormFields = SignUpFields & MarketingFields;

const marketingSignUpFormValidationSchema = yup.object({
  firstName: yup.string().required('Required').max(100),
  lastName: yup.string().required('Required').max(100),
  email: yup.string().email('Please enter a valid email address').required('Required'),
  phoneNumber: yup.string().required('Required').max(100),
  company: yup.string().required('Required').max(100),
  country: yup.string().required('Required'),
  state: yup.string().when('country', {
    is: (country: COUNTRIES_WITH_STATES) => Object.values(COUNTRIES_WITH_STATES).includes(country),
    then: yup.string().required('Required'),
  }),
  numberOfEmployees: yup.string().required('Required'),
  password: yup.string().required('Required').min(6, 'Password must be at least 6 characters long').max(100),
});

const marketingSignUpFormInitialValues: MarketingSignUpFormFields = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  company: '',
  country: '',
  state: '',
  numberOfEmployees: '',
  password: '',
};

/**
 * Standalone Sign Up page that allows user to register account at DataSet also known as Trial form
 * Collects additional fields such as name, phone, company, utm_*, etc… for marketing purposes. Values submitted to HubSpot via Form API
 *
 * @example
 * // insert SignUp form on DataSet marketing page
 *
 * // for US website:
 * <iframe height={620} width={460} frameBorder={0} title="DataSet US Sign Up Form" src="https://app.scalyr.com/signUpForm" />
 */
function SignUpForm(): JSX.Element {
  const [error, setError] = useState<Error>();
  const marketoScriptLoaded = useMarketo(MARKETO_BASE_URL, MARKETO_MUNCHKIN_ID, TRIAL_FORM_ID);

  // TBD - FRONT-7307 Temporarily hide "Sign Up Form" new domains
  const isNewDomain = K.NEW_DOMAINS.includes(window.location.hostname);

  const goBack = (): void => {
    history.back();
  };

  if (isNewDomain) {
    return (
      <div className="FormPages__MessageContainer">
        Sign up process is not currently supported, we are working on it.
        <a onClick={goBack}>Go Back</a>
      </div>
    );
  }

  async function signUpNewUser(values: SignUpFields) {
    const { email, password } = values;

    const SIGN_UP = gql`
      mutation signUpWithLoginAndPassword($emailAddress: String!, $password: String!, $clientTimeZoneOffset: String!) {
        signUpWithLoginAndPassword(
          emailAddress: $emailAddress
          password: $password
          clientTimeZoneOffset: $clientTimeZoneOffset
        )
      }
    `;

    await gqlRequest({
      mutation: SIGN_UP,
      variables: {
        emailAddress: email,
        password,
        clientTimeZoneOffset: new Date().getTimezoneOffset(),
      },
    });
  }
  async function handleFormSubmit(values: MarketingSignUpFormFields) {
    const { email, firstName, lastName, phoneNumber, company, country, numberOfEmployees, state } = values;
    const urlParams = new URLSearchParams(window.self.location.search);
    try {
      await signUpNewUser(values);
      if (marketoScriptLoaded) {
        const mktoForm = window.MktoForms2.allForms()[0];
        mktoForm.setValues({
          Email: email,
          FirstName: firstName,
          LastName: lastName,
          Phone: phoneNumber,
          Company: company,
          Country: country,
          Employees__c: numberOfEmployees,
          State: state,
          UTM_Campaign__c: urlParams.get('UTM_Campaign'),
          UTM_Content__c: urlParams.get('UTM_Content'),
          UTM_Medium__c: urlParams.get('UTM_Medium'),
          UTM_Source__c: urlParams.get('UTM_Source'),
          UTM_Term__c: urlParams.get('UTM_Term'),
          GCLID__c: urlParams.get('gclid'),
          WEBSITE_OPT_IN__c: urlParams.get('website_opt_in'),
        });
        mktoForm.submit();
      }
    } catch (err) {
      setError(err);
    }
  }
  return (
    <Formik
      validationSchema={marketingSignUpFormValidationSchema}
      validateOnChange={false}
      onSubmit={handleFormSubmit}
      initialValues={marketingSignUpFormInitialValues}
    >
      {({ handleChange, values, touched, errors, isSubmitting, submitForm }): JSX.Element => (
        <div id="SignUpForm" className="FormPages__Container">
          {error && <div className="error-message-block form-group">{error?.message}</div>}
          <MarketingForm values={values} touched={touched} errors={errors} handleChange={handleChange} />
          <Input
            type="password"
            name="password"
            label="Password *"
            onChange={handleChange}
            value={values.password}
            isInvalid={touched.password && !!errors.password}
            errorMessage={errors.password}
            testId={ELEMENT.signUpFormPasswordInput}
          />
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              setError(undefined);
              submitForm();
            }}
            disabled={isSubmitting}
            className="FormPages__Button"
            testId={ELEMENT.marketingFormContinueButton}
          >
            Try DataSet
          </Button>
          <form hidden id="mktoForm_6502"></form>
        </div>
      )}
    </Formik>
  );
}

export default SignUpForm;
